import { createContext, useState, useContext, ReactNode } from 'react';
import { Game, GameProgress } from '../types';

interface GameContextType {
  games: Game[];
  progress: GameProgress[];
  updateProgress: (gameId: string, score: number) => void;
  getGameById: (id: string) => Game | undefined;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

export const useGameContext = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGameContext must be used within a GameProvider');
  }
  return context;
};

interface GameProviderProps {
  children: ReactNode;
  initialGames: Game[];
}

export const GameProvider = ({ children, initialGames }: GameProviderProps) => {
  const [games] = useState<Game[]>(initialGames);
  const [progress, setProgress] = useState<GameProgress[]>([]);

  const updateProgress = (gameId: string, score: number) => {
    const now = new Date();
    const existingProgressIndex = progress.findIndex(p => p.gameId === gameId);

    if (existingProgressIndex >= 0) {
      // Update existing progress
      const updatedProgress = [...progress];
      updatedProgress[existingProgressIndex] = {
        ...updatedProgress[existingProgressIndex],
        score,
        lastPlayed: now
      };
      setProgress(updatedProgress);
    } else {
      // Add new progress
      setProgress([
        ...progress,
        {
          gameId,
          userId: 1, // Default user ID
          score,
          lastPlayed: now
        }
      ]);
    }
  };

  const getGameById = (id: string) => {
    return games.find(game => game.id === id);
  };

  return (
    <GameContext.Provider value={{ games, progress, updateProgress, getGameById }}>
      {children}
    </GameContext.Provider>
  );
};
